import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { g, p, pu, pa, del, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePlan } from "@/context/PlanContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Save, Trash2, Move, Pencil, Check, X, ChevronLeft, ChevronRight, Lock, Users } from "lucide-react";

const PRESETS = ["Sala", "Sala 1", "Sala 2", "Sala A", "Sala B", "Giardino", "Terrazza", "Dehors", "Veranda", "Bancone", "Privé", "Gazebo", "Spiaggia"];
const SIZES = [
  { id: "S", label: "Piccolo", w: 15, seats: 2 },
  { id: "M", label: "Medio", w: 21, seats: 4 },
  { id: "L", label: "Grande", w: 27, seats: 6 },
  { id: "XL", label: "Tavolata", w: 34, seats: 10 },
];
const SHAPES = [
  { id: "square", label: "Quadrato" },
  { id: "rect", label: "Rettangolare" },
  { id: "round", label: "Rotondo" },
];
export const sizeWidth = (s) => (SIZES.find((x) => x.id === s) || SIZES[1]).w;
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Piantina() {
  const { zones, refresh } = useApp();
  const { has, handleError, plan, refreshPlan } = usePlan();
  const pro = has("floorplan");
  const lim = plan?.limits || {};
  const use = plan?.usage || {};
  const [tables, setTables] = useState([]);
  const [zone, setZone] = useState(null);
  const [zoneName, setZoneName] = useState("");
  const [rename, setRename] = useState(null); // { id, name }
  const [nt, setNt] = useState({ size: "M", shape: "square" });
  const [drag, setDrag] = useState(null);
  const [selected, setSelected] = useState(null);
  const [bulk, setBulk] = useState(null);
  const [esito, setEsito] = useState(null);
  const box = useRef(null);

  const load = useCallback(() => g("/tables").then(setTables), []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (zones.length && !zones.some((z) => z.name === zone)) setZone(zones[0].name);
  }, [zones, zone]);

  const list = tables.filter((t) => t.zone === zone);
  const sel = list.find((t) => t.id === selected);
  const zObj = zones.find((z) => z.name === zone);
  const conta = (n) => tables.filter((t) => t.zone === n).length;

  const boom = (e) => {
    if (handleError(e)) return;
    toast.error(errMsg(e));
  };

  const move = (e) => {
    if (!drag || !box.current) return;
    const r = box.current.getBoundingClientRect();
    const point = e.touches?.[0] || e;
    const x = Math.min(96 - 10, Math.max(0, Math.round(((point.clientX - r.left) / r.width) * 100)));
    const y = Math.min(88, Math.max(0, Math.round(((point.clientY - r.top) / r.height) * 100)));
    setTables((ts) => ts.map((t) => (t.id === drag ? { ...t, x, y } : t)));
  };

  const patchLocal = (id, body) => setTables((ts) => ts.map((t) => (t.id === id ? { ...t, ...body } : t)));

  const save = async () => {
    try {
      await pu("/tables/layout", {
        tables: list.map(({ id, x, y, seats, shape, size, number }) => ({ id, x, y, seats, shape, size, number })),
      });
      toast.success("Piantina salvata");
      load();
    } catch (e) {
      boom(e);
    }
  };

  const sposta = async (dir) => {
    const i = zones.findIndex((z) => z.name === zone);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= zones.length) return;
    const ids = zones.map((z) => z.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      await pu("/zones/order", { ids });
      refresh();
    } catch (e) {
      boom(e);
    }
  };

  const salvaNome = async () => {
    const nuovo = rename.name.trim();
    if (!nuovo) return toast.error("Scrivi il nuovo nome della sala");
    if (nuovo === rename.was) return setRename(null);
    try {
      const r = await pa(`/zones/${rename.id}`, { name: nuovo });
      toast.success(`Ora si chiama "${nuovo}"`, {
        description: r.tables ? `${r.tables} tavoli restano dove sono, col loro nome` : undefined,
      });
      setRename(null);
      setZone(nuovo);
      refresh();
      load();
    } catch (e) {
      boom(e);
    }
  };

  return (
    <div className="fade-up space-y-5" data-testid="piantina-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Il tuo locale</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Sale e tavoli</h1>
          <p className="mt-1 text-sm text-slate-500">
            Crea le tue sale con i nomi che usate voi. Il tavolo prende la sigla della sala
            ({zObj?.prefix || "S"}1, {zObj?.prefix || "S"}2…) oppure il nome che scrivi tu: «Forno», «Vetrina», «Tavolone».
          </p>
          {plan?.plan_name && (
            <p className="mt-1 text-xs font-semibold text-slate-400" data-testid="zone-limits">
              Piano {plan?.plan_name}: {lim.max_zones ? `${use.zones}/${lim.max_zones} sale` : "sale illimitate"} ·{" "}
              {lim.max_tables ? `${use.tables}/${lim.max_tables} tavoli` : "tavoli illimitati"}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setBulk("")} data-testid="rinomina-blocco-btn">
            <Pencil className="mr-1.5 h-4 w-4" /> Rinomina in blocco
          </Button>
          {pro && (
            <Button onClick={save} data-testid="save-layout-btn">
              <Save className="mr-1.5 h-4 w-4" /> Salva piantina
            </Button>
          )}
        </div>
      </div>

      <Card className="space-y-3 p-4" data-testid="zone-bar">
        <div className="flex flex-wrap items-center gap-2">
          {zones.map((z) => (
            <button
              key={z.id}
              data-testid={`piantina-zone-${slug(z.name)}`}
              onClick={() => setZone(z.name)}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                zone === z.name ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {z.name}{" "}
              <span className="font-num text-[10px] opacity-60">
                {z.prefix} · {conta(z.name)}
              </span>
            </button>
          ))}
          {!zones.length && <p className="text-sm text-slate-500">Nessuna sala: creane una qui sotto.</p>}
        </div>

        {zObj && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            {rename?.id === zObj.id ? (
              <>
                <Input
                  className="h-9 w-44"
                  autoFocus
                  value={rename.name}
                  onChange={(e) => setRename({ ...rename, name: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && salvaNome()}
                  data-testid="zone-rename-input"
                />
                <Button size="sm" onClick={salvaNome} data-testid="zone-rename-save">
                  <Check className="mr-1 h-3.5 w-3.5" /> Salva nome
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRename(null)} data-testid="zone-rename-cancel">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <>
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{zObj.name}</span>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="zone-rename-btn"
                  onClick={() => setRename({ id: zObj.id, name: zObj.name, was: zObj.name })}
                >
                  <Pencil className="mr-1 h-3.5 w-3.5" /> Rinomina
                </Button>
                <Button size="sm" variant="outline" data-testid="zone-move-left" onClick={() => sposta(-1)}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" data-testid="zone-move-right" onClick={() => sposta(1)}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-rose-600"
                  data-testid="delete-zone-btn"
                  onClick={async () => {
                    if (!window.confirm(`Elimino la sala "${zObj.name}"?`)) return;
                    try {
                      await del(`/zones/${zObj.id}`);
                      toast.success("Sala eliminata");
                      setZone(null);
                      refresh();
                    } catch (e) {
                      boom(e);
                    }
                  }}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" /> Elimina sala
                </Button>
                <span className="text-xs text-slate-400">Le frecce cambiano l'ordine delle linguette in sala</span>
              </>
            )}
          </div>
        )}
      </Card>

      <Card className="space-y-3 p-4" data-testid="new-zone-bar">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Nuova sala</span>
          {PRESETS.filter((x) => !zones.some((z) => z.name === x))
            .slice(0, 8)
            .map((x) => (
              <button
                key={x}
                data-testid={`zone-preset-${slug(x)}`}
                onClick={() => setZoneName(x)}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200"
              >
                {x}
              </button>
            ))}
          <Input
            className="h-9 w-44"
            placeholder="Oppure scrivi il nome"
            value={zoneName}
            onChange={(e) => setZoneName(e.target.value)}
            data-testid="zone-name-input"
          />
          <Button
            size="sm"
            data-testid="add-zone-btn"
            onClick={async () => {
              if (!zoneName.trim()) return toast.error("Scrivi il nome della sala");
              try {
                const z = await p("/zones", { name: zoneName.trim() });
                toast.success(`Sala "${z.name}" creata`, { description: `I tavoli si chiameranno ${z.prefix}1, ${z.prefix}2…` });
                setZoneName("");
                setZone(z.name);
                refresh();
                refreshPlan();
              } catch (e) {
                boom(e);
              }
            }}
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Crea sala
          </Button>
        </div>

        {zone && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Nuovo tavolo in {zone}</span>
            <Input
              className="h-9 w-40"
              placeholder="Nome (facoltativo)"
              value={nt.number || ""}
              onChange={(e) => setNt({ ...nt, number: e.target.value })}
              data-testid="new-table-name-input"
            />
            {SIZES.map((s) => (
              <Button
                key={s.id}
                size="sm"
                variant={nt.size === s.id ? "default" : "outline"}
                onClick={() => setNt({ ...nt, size: s.id })}
                data-testid={`new-size-${s.id}`}
              >
                {s.label} · {s.seats}p
              </Button>
            ))}
            {pro &&
              SHAPES.map((s) => (
                <Button
                  key={s.id}
                  size="sm"
                  variant={nt.shape === s.id ? "default" : "outline"}
                  onClick={() => setNt({ ...nt, shape: s.id })}
                  data-testid={`new-shape-${s.id}`}
                >
                  {s.label}
                </Button>
              ))}
            <Button
              size="sm"
              data-testid="add-table-btn"
              onClick={async () => {
                const seats = SIZES.find((s) => s.id === nt.size).seats;
                try {
                  const t = await p("/tables", { zone, seats, size: nt.size, shape: nt.shape, x: 6, y: 6,
                                                 number: (nt.number || "").trim() });
                  toast.success(`Tavolo ${t.number} aggiunto in ${zone}`);
                  setNt({ ...nt, number: "" });
                  load();
                  refreshPlan();
                } catch (e) {
                  boom(e);
                }
              }}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi tavolo
            </Button>
          </div>
        )}
      </Card>

      {pro ? (
        <Card
          ref={box}
          className="relative h-[440px] select-none overflow-hidden bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:20px_20px] sm:h-[560px]"
          onMouseMove={move}
          onMouseUp={() => setDrag(null)}
          onMouseLeave={() => setDrag(null)}
          onTouchMove={move}
          onTouchEnd={() => setDrag(null)}
          data-testid="piantina-editor"
        >
          {list.map((t) => (
            <div
              key={t.id}
              data-testid={`table-${t.number}`}
              onMouseDown={() => {
                setDrag(t.id);
                setSelected(t.id);
              }}
              onTouchStart={() => {
                setDrag(t.id);
                setSelected(t.id);
              }}
              className={`absolute cursor-move border-2 bg-white p-2 shadow-sm ${
                t.shape === "round" ? "rounded-full" : "rounded-lg"
              } ${selected === t.id ? "border-amber-500 ring-2 ring-amber-200" : "border-slate-900"}`}
              style={{ left: `${t.x}%`, top: `${t.y}%`, width: `${sizeWidth(t.size)}%` }}
            >
              <p className="font-num truncate text-sm font-bold">{t.number}</p>
              <p className="text-[10px] text-slate-500">{t.seats} posti</p>
            </div>
          ))}
          {!list.length && (
            <p className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
              Nessun tavolo in {zone || "questa sala"}: aggiungine uno qui sopra.
            </p>
          )}
        </Card>
      ) : (
        <Card className="p-4" data-testid="tables-list">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Tavoli in {zone || "sala"}</p>
          <div className="mt-3 divide-y divide-slate-100">
            {list.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`table-row-${t.number}`}>
                <Input
                  className="font-num h-9 w-24 font-bold"
                  defaultValue={t.number}
                  onBlur={async (e) => {
                    const number = e.target.value.trim();
                    if (!number || number === t.number) return;
                    try {
                      await pa(`/tables/${t.id}`, { number });
                      toast.success(`Ora si chiama ${number}`);
                      load();
                    } catch (err) {
                      boom(err);
                      load();
                    }
                  }}
                  data-testid={`table-number-${t.number}`}
                />
                <Users className="h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  min="1"
                  className="h-9 w-20"
                  defaultValue={t.seats}
                  onBlur={async (e) => {
                    const seats = Number(e.target.value) || 1;
                    if (seats === t.seats) return;
                    try {
                      await pa(`/tables/${t.id}`, { seats });
                      load();
                    } catch (err) {
                      boom(err);
                    }
                  }}
                  data-testid={`table-seats-${t.number}`}
                />
                <span className="text-xs text-slate-400">posti</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto text-rose-600"
                  data-testid={`delete-table-${t.number}`}
                  onClick={async () => {
                    try {
                      await del(`/tables/${t.id}`);
                      toast.success("Tavolo eliminato");
                      load();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            {!list.length && <p className="py-3 text-sm text-slate-500">Nessun tavolo qui: aggiungine uno qui sopra.</p>}
          </div>
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Sale e tavoli li gestisci su ogni piano. Con il piano <strong>Pro</strong> arriva la piantina
              trascinabile: disponi i tavoli come sono davvero nel locale, con forma e dimensione.
            </span>
          </div>
        </Card>
      )}

      {pro && sel && (
        <Card className="flex flex-wrap items-center gap-2 p-4" data-testid="table-editor">
          <Input
            className="font-num h-9 w-24 font-bold"
            defaultValue={sel.number}
            key={sel.id}
            onBlur={async (e) => {
              const number = e.target.value.trim();
              if (!number || number === sel.number) return;
              try {
                await pa(`/tables/${sel.id}`, { number });
                toast.success(`Ora si chiama ${number}`);
                load();
              } catch (err) {
                boom(err);
                load();
              }
            }}
            data-testid="edit-number-input"
          />
          <span className="text-xs text-slate-500">
            <Move className="mr-1 inline h-3 w-3" /> trascinalo nella piantina
          </span>
          {SIZES.map((s) => (
            <Button
              key={s.id}
              size="sm"
              variant={sel.size === s.id ? "default" : "outline"}
              data-testid={`edit-size-${s.id}`}
              onClick={() => patchLocal(sel.id, { size: s.id, seats: s.seats })}
            >
              {s.label}
            </Button>
          ))}
          {SHAPES.map((s) => (
            <Button
              key={s.id}
              size="sm"
              variant={sel.shape === s.id ? "default" : "outline"}
              data-testid={`edit-shape-${s.id}`}
              onClick={() => patchLocal(sel.id, { shape: s.id })}
            >
              {s.label}
            </Button>
          ))}
          <Input
            type="number"
            min="1"
            className="h-9 w-20"
            value={sel.seats}
            onChange={(e) => patchLocal(sel.id, { seats: Number(e.target.value) || 1 })}
            data-testid="edit-seats-input"
          />
          <Button
            size="sm"
            variant="ghost"
            className="text-rose-600"
            data-testid={`delete-table-${sel.number}`}
            onClick={async () => {
              try {
                await del(`/tables/${sel.id}`);
                toast.success("Tavolo eliminato");
                setSelected(null);
                load();
              } catch (e) {
                toast.error("Tavolo con comanda aperta");
              }
            }}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Elimina
          </Button>
        </Card>
      )}
      {bulk !== null && (
        <Card className="border-2 border-slate-900 p-5" data-testid="bulk-rename-card">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Rinomina in blocco</p>
          <h2 className="mt-1 text-lg font-bold">Rimetti tanti nomi in una volta</h2>
          <p className="mt-1 text-sm text-slate-500">
            Una riga per tavolo: <b>nome di adesso = nome nuovo</b>. Esempio: <code>S1 = Div 1</code>
          </p>
          <textarea
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            rows={8}
            spellCheck={false}
            placeholder={"S1 = Div 1\nS2 = Tav 1\nSA6 = Centrale"}
            className="mt-3 w-full rounded-md border-2 border-slate-200 p-3 font-mono text-sm focus:border-slate-900 focus:outline-none"
            data-testid="bulk-rename-input"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              data-testid="bulk-rename-save"
              onClick={async () => {
                try {
                  const r = await p("/tables/rename-bulk", { pairs: bulk });
                  setEsito(r);
                  toast.success(`${r.fatti.length} tavoli rinominati`, {
                    description: r.saltati.length ? `${r.saltati.length} righe non capite` : undefined,
                  });
                  load();
                  if (!r.saltati.length) setBulk(null);
                } catch (e) {
                  boom(e);
                }
              }}
            >
              <Check className="mr-1.5 h-4 w-4" /> Rinomina tutti
            </Button>
            <Button variant="ghost" onClick={() => { setBulk(null); setEsito(null); }} data-testid="bulk-rename-close">
              Chiudi
            </Button>
          </div>
          {esito?.saltati?.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-amber-800" data-testid="bulk-rename-skipped">
              {esito.saltati.map((s, i) => (
                <li key={i}>
                  <b>{s.riga}</b> — {s.perche}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
